import { useEffect, useState, useCallback } from "react";
import { CreditCard, AlertTriangle, RefreshCw, Video, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const CARDS = [
  ["active_checkouts", "Active checkouts"], ["paid", "Paid"], ["failed", "Failed"], ["cancelled", "Cancelled"],
  ["active_reservations", "Active reservations"], ["expired_active_reservations", "Stuck reservations"],
  ["paid_no_order", "Paid · no order"], ["amount_mismatch", "Amount mismatch"],
];
const DANGER = new Set(["expired_active_reservations", "paid_no_order", "amount_mismatch", "order_missing"]);

export default function AdminCheckout() {
  const [data, setData] = useState(null);
  const [rp, setRp] = useState(null);
  const [savingRp, setSavingRp] = useState(false);
  const load = useCallback(() => {
    api.get("/marketplace/admin/checkout/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, []);
  const loadRp = useCallback(() => {
    api.get("/marketplace/admin/return-policy").then((r) => setRp(r.data.policy)).catch(() => {});
  }, []);
  useEffect(() => { load(); loadRp(); }, [load, loadRp]);

  const saveRp = async () => {
    setSavingRp(true);
    try {
      const r = await api.put("/marketplace/admin/return-policy", rp);
      setRp(r.data.policy);
      toast.success("Return & refund notice updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingRp(false); }
  };

  const m = data?.metrics || {};
  const exc = data?.exceptions || [];
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Checkout & Payment Health" icon={CreditCard}
        subtitle="Reconciliation across checkout → payment → order → inventory. Monitoring only — financial records are immutable and corrections require an audited workflow."
        actions={<button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600" data-testid="admin-checkout-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>} />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-checkout-metrics">
        {CARDS.map(([k, l]) => (
          <div key={k} className="card-soft p-4"><p className="text-xs text-slate-500">{l}</p><p className={`text-2xl font-bold ${DANGER.has(k) ? (m[k] ? "text-rose-600" : "text-slate-400") : "text-slate-800"}`}>{m[k] ?? 0}</p></div>
        ))}
      </div>

      {data?.note && <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">{data.note}</p>}

      {rp && (
        <div className="card-soft mb-4 p-5" data-testid="return-policy-editor">
          <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Video className="h-4 w-4 text-amber-500" /> Return &amp; Refund video notice</p>
          <p className="mb-3 text-xs text-slate-500">Tells buyers an open-box (unboxing) video is required for returns/refunds. Edit the wording and choose exactly where it appears.</p>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Heading</label>
          <input value={rp.title || ""} onChange={(e) => setRp({ ...rp, title: e.target.value })} data-testid="rp-title" className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <label className="mb-1 block text-xs font-semibold text-slate-600">Message</label>
          <textarea value={rp.text || ""} onChange={(e) => setRp({ ...rp, text: e.target.value })} data-testid="rp-text" rows={3} className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm leading-relaxed" />
          <div className="mb-3 grid gap-2 sm:grid-cols-2">
            {[["enabled", "Show this notice (master switch)"], ["show_checkout", "On checkout — Order Summary"], ["show_orders", "On orders / payment-success page"], ["show_email", "In the order confirmation email"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!rp[k]} onChange={(e) => setRp({ ...rp, [k]: e.target.checked })} data-testid={`rp-${k}`} className="h-4 w-4 accent-amber-600" /> {l}</label>
            ))}
          </div>
          <p className="mb-1.5 text-xs font-semibold text-slate-500">Live preview</p>
          <div className="rounded-xl border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 p-4" data-testid="rp-preview">
            <p className="flex items-center gap-2 text-sm font-bold text-amber-800"><Video className="h-4 w-4 shrink-0" /> {rp.title || "Return & Refund verification"}</p>
            <p className="mt-1 text-sm leading-relaxed text-amber-900/90">{rp.text}</p>
          </div>
          <button onClick={saveRp} disabled={savingRp} data-testid="rp-save" className="mt-3 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" /> {savingRp ? "Saving…" : "Save notice"}</button>
        </div>
      )}

      <div className="card-soft p-5" data-testid="admin-checkout-exceptions">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-amber-500" /> Reconciliation exceptions {exc.length ? `(${exc.length})` : ""}</p>
        {data === null ? <div className="h-16 animate-pulse rounded-lg bg-slate-100" /> : exc.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No reconciliation exceptions — payments, orders and inventory are consistent.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="text-left text-xs uppercase text-slate-400"><tr><th className="p-2">Type</th><th className="p-2">Payment / Order</th><th className="p-2 text-right">Detail</th></tr></thead>
              <tbody>
                {exc.map((e, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid="checkout-exc-row">
                    <td className="p-2"><span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">{e.type.replace(/_/g, " ")}</span></td>
                    <td className="p-2 font-mono text-xs text-slate-600">{e.number || e.order_id}</td>
                    <td className="p-2 text-right text-slate-600">{e.type === "amount_mismatch" ? `order ₹${e.order_total} vs paid ₹${e.paid}` : e.amount != null ? `₹${e.amount}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
